import React, { useMemo, useState } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { useVenue } from "../VenueProvider";
import { Section, formatMXN, ACCENT } from "../cuentas/shared";
import { Kpi, VALOR_COLOR, diaLocalDeAncla } from "./shared";
import { ocupacionPorEspacio } from "../../utils/reportes";
import {
  buildOccupiedSet,
  rangoOcupacion,
  ocupacionPorDiaSemana,
  yearsInSet,
} from "../../utils/ocupacion";
import {
  CalendarDaysIcon,
  Squares2X2Icon,
  InformationCircleIcon,
} from "@heroicons/react/20/solid";

const FILTROS = [
  { key: "todos", label: "Todos los días" },
  { key: "finde", label: "Fines de semana" },
  { key: "semana", label: "Entre semana" },
];

// Qué tan lleno estuviste y cuál de tus espacios carga el negocio.
//
// La ocupación por día usa la misma regla que el Inicio (utils/ocupacion.js):
// un evento ocupa TODOS los días de su rango, y en el filtro de fines de
// semana la unidad es el finde completo, no el día suelto — "3 de 5 fines
// de semana" es como piensa un venue.
const ReporteOcupacion = ({ rango }) => {
  const { events, espaciosActivos, multiespacioActivo, calendarTimezone: tz } =
    useVenue();
  const [filtro, setFiltro] = useState("todos");

  const occupiedSet = useMemo(() => buildOccupiedSet(events), [events]);

  // rangoOcupacion trabaja en días LOCALES y con el fin exclusivo; el
  // período llega como ancla UTC e inclusivo, de ahí la conversión.
  const ocupacion = useMemo(
    () =>
      rangoOcupacion(
        occupiedSet,
        diaLocalDeAncla(rango.from),
        diaLocalDeAncla(rango.to, 1),
        filtro
      ),
    [occupiedSet, rango, filtro]
  );

  const previa = useMemo(
    () =>
      rango.prev
        ? rangoOcupacion(
            occupiedSet,
            diaLocalDeAncla(rango.prev.from),
            diaLocalDeAncla(rango.prev.to, 1),
            filtro
          )
        : null,
    [occupiedSet, rango, filtro]
  );

  const porEspacio = useMemo(
    () =>
      ocupacionPorEspacio({
        events,
        espacios: espaciosActivos,
        desde: rango.from,
        hasta: rango.to,
        timeZone: tz,
      }),
    [events, espaciosActivos, rango, tz]
  );

  const porDiaSemana = useMemo(() => {
    const years = yearsInSet(occupiedSet).slice(0, 2);
    return { years, filas: years.length > 0 ? ocupacionPorDiaSemana(occupiedSet, years) : [] };
  }, [occupiedSet]);

  const pct = ocupacion.total > 0 ? ocupacion.ocupados / ocupacion.total : 0;
  const pctPrev =
    previa && previa.total > 0 ? previa.ocupados / previa.total : null;
  const maxEventos = porEspacio.filas[0]?.eventos || 0;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap gap-1.5">
        {FILTROS.map((f) => (
          <button
            key={f.key}
            type="button"
            onClick={() => setFiltro(f.key)}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition ${
              filtro === f.key
                ? "text-white shadow-sm"
                : "text-zinc-500 bg-zinc-100 dark:bg-zinc-700/60 hover:text-zinc-700"
            }`}
            style={filtro === f.key ? { backgroundColor: ACCENT } : undefined}
          >
            {f.label}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Kpi
          label="Ocupación"
          value={`${Math.round(pct * 100)}%`}
          delta={pctPrev !== null && pctPrev > 0 ? (pct - pctPrev) / pctPrev : null}
          hint={`${ocupacion.ocupados} de ${ocupacion.total} ${ocupacion.unidad}`}
        />
        <Kpi
          label="Eventos"
          value={porEspacio.eventos}
          hint="en el período, sin contar cancelados"
        />
        <Kpi
          label="Valor del período"
          value={formatMXN(porEspacio.valor)}
          hint="de esos eventos"
        />
        <Kpi
          label="Valor por evento"
          value={formatMXN(
            porEspacio.eventos > 0 ? porEspacio.valor / porEspacio.eventos : 0
          )}
          hint="promedio del período"
        />
      </div>

      {multiespacioActivo && (
        <Section icon={Squares2X2Icon} title="Por espacio">
          {porEspacio.filas.length === 0 ? (
            <p className="text-sm text-zinc-400">
              Este venue todavía no tiene espacios dados de alta.
            </p>
          ) : (
            <>
              <div className="space-y-3">
                {porEspacio.filas.map((f) => (
                  <div key={f.id}>
                    <div className="flex items-baseline justify-between text-sm">
                      <span className="truncate text-zinc-600 dark:text-zinc-300">
                        {f.nombre}
                      </span>
                      <span className="font-medium text-zinc-900 dark:text-white whitespace-nowrap">
                        {f.eventos} {f.eventos === 1 ? "evento" : "eventos"}
                        <span className="ml-1.5 text-xs font-normal text-zinc-400">
                          {formatMXN(f.valor)} · {formatMXN(f.valorPorEvento)} c/u
                        </span>
                      </span>
                    </div>
                    <div className="mt-1 h-2 w-full rounded-full bg-zinc-100 dark:bg-zinc-700/60 overflow-hidden">
                      <div
                        className="h-full rounded-full"
                        style={{
                          width: `${maxEventos > 0 ? (f.eventos / maxEventos) * 100 : 0}%`,
                          backgroundColor: ACCENT,
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>
              {porEspacio.sinFootprint > 0 && (
                <div className="mt-4 flex gap-2 text-xs text-zinc-500">
                  <InformationCircleIcon className="h-4 w-4 shrink-0 text-zinc-400" />
                  <span>
                    {porEspacio.sinFootprint} de {porEspacio.eventos} eventos no
                    tienen espacio asignado, así que ocupan el venue entero y
                    cuentan para todos los renglones de arriba. Mientras sean
                    muchos, este desglose dice poco.
                  </span>
                </div>
              )}
            </>
          )}
        </Section>
      )}

      {porDiaSemana.filas.length > 0 && (
        <Section icon={CalendarDaysIcon} title="Ocupación por día de la semana">
          <ResponsiveContainer width="100%" height={240}>
            <BarChart
              data={porDiaSemana.filas}
              margin={{ top: 10, right: 12, left: -12, bottom: 0 }}
            >
              <CartesianGrid strokeDasharray="3 3" className="stroke-zinc-200 dark:stroke-zinc-700" />
              <XAxis dataKey="dia" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `${v}%`} unit="%" />
              <Tooltip
                formatter={(v) => `${v}%`}
                contentStyle={{ fontSize: 12, borderRadius: 8 }}
              />
              {porDiaSemana.years.map((y, i) => (
                <Bar
                  key={y}
                  dataKey={y}
                  name={String(y)}
                  fill={i === 0 ? ACCENT : VALOR_COLOR}
                  radius={[4, 4, 0, 0]}
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
          <p className="mt-2 text-xs text-zinc-400">
            Los últimos dos años con eventos, en porcentaje de los días de cada
            tipo. Este desglose ignora el filtro de arriba a propósito: es
            justamente el análisis por tipo de día.
          </p>
        </Section>
      )}
    </div>
  );
};

export default ReporteOcupacion;
